/**
 * Cognito Authentication Service
 *
 * Transliterated from the Swift CognitoAuthService in mv-prototype.
 * Uses the same USER_PASSWORD_AUTH flow with deterministic username generation.
 */

import { COGNITO_ENDPOINT, COGNITO_CLIENT_ID } from '@/config/api';

interface AuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface AuthState {
  idToken: string;
  accessToken: string;
  memberId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  expiresAt: number; // Unix timestamp ms
}

const AUTH_STORAGE_KEY = 'ol_admin_auth';

/**
 * Generate deterministic Cognito username from lastName + barcode.
 * Must match the iOS/Android implementation exactly:
 *   username = lastName.toLowerCase() + "_" + SHA256(barcode).substring(0, 12)
 */
async function generateCognitoUsername(lastName: string, barcodeNumber: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(barcodeNumber);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  const shortHash = hashHex.substring(0, 12);
  return `${lastName.toLowerCase()}_${shortHash}`;
}

/**
 * Decode a JWT payload (base64url → JSON).
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT');
  // base64url → base64
  let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  // Pad to multiple of 4
  while (payload.length % 4) payload += '=';
  const json = atob(payload);
  return JSON.parse(json);
}

/**
 * Authenticate with Cognito using last name + barcode.
 */
export async function signIn(lastName: string, barcodeNumber: string): Promise<AuthState> {
  const cognitoUsername = await generateCognitoUsername(lastName, barcodeNumber);

  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: COGNITO_CLIENT_ID,
      AuthParameters: {
        USERNAME: cognitoUsername,
        PASSWORD: barcodeNumber,
      },
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const errorType = (errorBody as any).__type || 'UnknownError';
    if (errorType === 'NotAuthorizedException') {
      throw new Error('Invalid credentials. Check your last name and barcode.');
    }
    if (errorType === 'UserNotFoundException') {
      throw new Error('User not found. Check your last name and barcode.');
    }
    throw new Error(`Authentication failed: ${errorType}`);
  }

  const data = await response.json();
  const raw = data.AuthenticationResult;
  if (!raw) {
    throw new Error('No authentication result returned');
  }

  // Cognito returns PascalCase field names
  const authResult: AuthResult = {
    accessToken: raw.AccessToken,
    idToken: raw.IdToken,
    refreshToken: raw.RefreshToken,
    expiresIn: raw.ExpiresIn,
  };

  // Decode the ID token to extract user claims
  const claims = decodeJwtPayload(authResult.idToken);

  const authState: AuthState = {
    idToken: authResult.idToken,
    accessToken: authResult.accessToken,
    memberId: (claims.memberId as string) || '',
    firstName: (claims.firstName as string) || (claims.given_name as string) || '',
    lastName: (claims.lastName as string) || (claims.family_name as string) || '',
    email: (claims.email as string) || '',
    roles: parseRoles(claims),
    expiresAt: Date.now() + authResult.expiresIn * 1000,
  };

  // Persist to localStorage
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authState));

  return authState;
}

function parseRoles(claims: Record<string, unknown>): string[] {
  try {
    const rolesStr = claims.roles as string;
    if (rolesStr) return JSON.parse(rolesStr);
  } catch { /* ignore */ }
  return [];
}

/**
 * Get stored auth state, or null if not authenticated / expired.
 */
export function getAuth(): AuthState | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    const auth: AuthState = JSON.parse(stored);
    // Check expiry (with 5-minute buffer)
    if (auth.expiresAt < Date.now() + 5 * 60 * 1000) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
    return auth;
  } catch {
    return null;
  }
}

/**
 * Get the current JWT ID token for API calls, or null if not authenticated.
 */
export function getIdToken(): string | null {
  return getAuth()?.idToken || null;
}

/**
 * Sign out — clear stored auth.
 */
export function signOut(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}
